// ecommerce-frontend/src/pages/ResetPasswordPage.jsx

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff, X } from 'lucide-react'; // Ajout de X
import { authAPI } from '../services/api';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const uid = searchParams.get('uid');

  const [formData, setFormData] = useState({
    password: '',
    password2: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: '' });

  const evaluatePasswordStrength = (password) => {
    let score = 0;
    let message = '';
    let color = '';

    if (password.length === 0) {
      setPasswordStrength({ score: 0, message: '', color: '' });
      return;
    }

    const hasLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const notOnlyNumbers = !/^\d+$/.test(password);

    if (hasLength) score += 1;
    if (hasUpperCase) score += 1;
    if (hasLowerCase) score += 1;
    if (hasNumber) score += 1;
    if (hasSpecialChar) score += 1;
    if (notOnlyNumbers) score += 1;

    if (score <= 2) {
      message = 'Faible';
      color = 'red';
    } else if (score <= 4) {
      message = 'Moyen';
      color = 'yellow';
    } else {
      message = 'Fort';
      color = 'green';
    }

    setPasswordStrength({ score, message, color });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'password') {
      evaluatePasswordStrength(value);
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    // Effacer l'erreur générale lors de la saisie
    if (errors.general) {
        setErrors({ ...errors, general: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (/^\d+$/.test(formData.password)) {
      newErrors.password = 'Le mot de passe ne peut pas être entièrement numérique';
    } else if (passwordStrength.score < 3) {
      newErrors.password = 'Le mot de passe est trop faible';
    }

    if (formData.password !== formData.password2) {
      newErrors.password2 = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    if (!token || !uid) {
      setErrors({ general: 'Lien invalide ou expiré' });
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword({
        token,
        uid,
        new_password: formData.password,
      });
      setSuccess(true);
    } catch (err) {
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        const errorMessages = {
          'Invalid token': 'Lien invalide ou expiré',
          'Token has expired': 'Le lien a expiré',
          'password_too_common': 'Ce mot de passe est trop courant',
          'password_entirely_numeric': 'Le mot de passe ne peut pas être entièrement numérique',
        };

        let errorMsg = errorData.error || errorData.detail;
        
        if (errorMsg) {
          Object.keys(errorMessages).forEach(pattern => {
            if (errorMsg.includes(pattern)) {
              errorMsg = errorMessages[pattern];
            }
          });
          setErrors({ general: errorMsg });
        } else {
          setErrors({ general: 'Erreur lors de la réinitialisation' });
        }
      } else {
        setErrors({ general: 'Erreur de connexion au serveur' });
      }
    } finally {
      setLoading(false);
    }
  };

  const PasswordStrengthBar = () => {
    if (!formData.password) return null;

    const strengthColors = {
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500',
    };

    return (
      <div className="mt-2">
        <div className="flex gap-1 mb-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`h-1 flex-1 rounded ${
                level <= passwordStrength.score
                  ? strengthColors[passwordStrength.color]
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className={`text-xs text-${passwordStrength.color}-600`}>
          Force : {passwordStrength.message}
        </p>
      </div>
    );
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-500 rounded-full mb-4">
              <CheckCircle size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Mot de passe réinitialisé !</h2>
            <p className="text-gray-600 mb-6">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-indigo-500 text-white py-3 rounded-lg font-semibold hover:bg-indigo-600 transition"
            >
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-500 rounded-full mb-4">
            <Lock size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Nouveau mot de passe</h2>
          <p className="text-gray-600 mt-2">Créez un mot de passe sécurisé</p>
        </div>

        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-start justify-between">
            <div className='flex items-start'>
                <AlertCircle size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-red-700 text-sm">{errors.general}</span>
            </div>
            {/* Ajout du bouton de fermeture */}
            <button onClick={() => setErrors({...errors, general: ''})} className={`p-1 rounded-full text-red-500 hover:bg-red-100 transition-colors flex-shrink-0`}>
                <X size={16} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <PasswordStrengthBar />
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            <p className="text-xs text-gray-500 mt-1">
              Min. 8 caractères, majuscules, minuscules, chiffres et symboles
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type={showPassword2 ? 'text' : 'password'}
                name="password2"
                value={formData.password2}
                onChange={handleChange}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.password2 ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword2(!showPassword2)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword2 ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              {!errors.password2 && formData.password2 && formData.password === formData.password2 && (
                <CheckCircle className="absolute right-10 top-3 text-green-500" size={20} />
              )}
            </div>
            {errors.password2 && <p className="text-red-500 text-sm mt-1">{errors.password2}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-500 text-white py-3 rounded-lg font-semibold hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Réinitialisation...
              </>
            ) : (
              'Réinitialiser le mot de passe'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
}